import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const downloads = [
  {
    format: 'PDF',
    icon: '📄',
    label: 'PDF',
    description: 'Pre tlač a čítanie na počítači',
    filename: 'Ked peniaze prestanu strastit.pdf',
    bg: 'from-rose-500 to-pink-600',
    border: 'border-rose-200',
    hover: 'hover:shadow-rose-200',
  },
  {
    format: 'EPUB',
    icon: '📱',
    label: 'EPUB',
    description: 'Pre mobilné zariadenia a tablety',
    filename: 'Keď peniaze prestanú strašiť - Diana Bombuškárová.epub',
    bg: 'from-emerald-500 to-teal-600',
    border: 'border-emerald-200',
    hover: 'hover:shadow-emerald-200',
  },
  {
    format: 'MOBI',
    icon: '📚',
    label: 'MOBI',
    description: 'Pre Kindle a Amazon čítačky',
    filename: 'Keď peniaze prestanú strašiť - Diana Bombuškárová.mobi',
    bg: 'from-violet-500 to-purple-600',
    border: 'border-violet-200',
    hover: 'hover:shadow-violet-200',
  },
];

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const transactionId = params.get('t');
    const alreadyVerified = sessionStorage.getItem('payment_session_verified');

    // Allow refresh within same session
    if (alreadyVerified) {
      setVerifying(false);
      return;
    }

    if (!transactionId) {
      navigate('/', { replace: true });
      return;
    }

    // Server-side verification via Viva Wallet API
    fetch(`/api/verify-payment?t=${transactionId}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.verified) {
          navigate('/', { replace: true });
          return;
        }
        sessionStorage.setItem('payment_session_verified', 'true');
        sessionStorage.removeItem('pending_order_code');
        setVerifying(false);

        // Send confirmation email
        const customerEmail = localStorage.getItem('customer_email');
        if (customerEmail) {
          fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: customerEmail, transactionId }),
          })
            .then((r) => r.json())
            .then((r) => {
              if (r.sent) {
                setEmailSent(true);
                localStorage.removeItem('customer_email');
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        // If verify API fails, fall back to URL param check
        const vivaOrderCode = params.get('s');
        if (transactionId && vivaOrderCode) {
          sessionStorage.setItem('payment_session_verified', 'true');
        } else {
          navigate('/', { replace: true });
        }
        setVerifying(false);
      });
  }, [navigate, location.search]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-amber-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Overujeme platbu…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-amber-50 to-sky-50 flex flex-col">

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-blue-700 font-semibold hover:text-blue-900 transition-colors">
              ← Späť na hlavnú stránku
            </Link>
          </div>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl w-full text-center">

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl">
              <span className="text-4xl text-white font-bold">✓</span>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-3">
            Vaša platba bola úspešná
          </h1>
          <p className="text-xl sm:text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-amber-600 mb-4">
            Ste o krok bližšie k novým začiatkom.
          </p>
          <p className="text-gray-600 text-base mb-4 max-w-lg mx-auto leading-relaxed">
            Stiahnite si <strong className="text-gray-900">„Keď peniaze prestanú strašiť"</strong> vo formáte, ktorý vám najviac vyhovuje.
          </p>

          {/* Email sent badge */}
          {emailSent && (
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full mb-8">
              <span>✉️</span> Odkaz na stiahnutie sme vám zaslali aj e-mailom.
            </div>
          )}

          {/* Download cards */}
          <div className="grid sm:grid-cols-3 gap-5 mb-8">
            {downloads.map((item) => (
              <a
                key={item.format}
                href={`/${item.filename}`}
                download={item.filename}
                className="group flex flex-col items-center p-6 bg-white rounded-2xl shadow-md border border-blue-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <span className="text-5xl mb-3">{item.icon}</span>
                <span className="font-bold text-gray-900 text-xl mb-1">{item.label}</span>
                <span className="text-gray-500 text-xs text-center mb-5">{item.description}</span>
                <span className={`px-5 py-2 rounded-full bg-gradient-to-r ${item.bg} text-white text-sm font-semibold shadow-md group-hover:shadow-lg transition-all duration-200`}>
                  Stiahnuť
                </span>
              </a>
            ))}
          </div>

          {/* Tip */}
          <div className="bg-white border border-blue-100 rounded-xl p-4 mb-8 text-left shadow-sm">
            <p className="text-gray-600 text-sm">
              💡 <strong className="text-gray-900">Tip:</strong> Uložte si súbor na bezpečné miesto — tento odkaz zostane aktívny len počas aktuálnej relácie.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
